import UploadedIcon from '@assets/icons/document-check.svg?react'
import DeleteIcon from '@assets/icons/trash.svg?react'
import UploadIcon from '@assets/icons/upload.svg?react'
import settings from '@settings'
import cn from 'classnames'
import { useTransactionFilesManagementQry, useTransactionFileUploadQry } from 'mmfintech-backend-api'
import type { TransactionDocumentType, UploadedTransactionDocumentResponse } from 'mmfintech-backend-api/types'
import { checkFileUpload, isValidFileArrayOrFileList, tr } from 'mmfintech-commons'
import type { ErrorObjectType, UploadFileError } from 'mmfintech-commons/types'
import { DragAndDrop, ErrorDisplay, FileInput } from 'mmfintech-portal-commons'
import { useState } from 'react'
import styles from './FileUploadInput.module.scss'

type FileUploadInputProps = {
  className?: string
  name?: string
  label?: string
  documentType?: TransactionDocumentType
  onChange?: any
  error?: string
  'data-test'?: string
}

export const FileUploadInput = (props: FileUploadInputProps) => {
  const { className, name, label, documentType = 'OTHER', onChange, error, 'data-test': dataTest } = props

  const [file, setFile] = useState<UploadedTransactionDocumentResponse | null>(null)
  const [fileError, setFileError] = useState<ErrorObjectType | null>(null)

  const { uploadFile, uploadFileError /*, uploadFileLoading*/ } = useTransactionFileUploadQry()
  const { removeFile } = useTransactionFilesManagementQry()

  const handleFileUploaded = (response: UploadedTransactionDocumentResponse) => {
    setFile(response)
    onChange(name, response.id)
  }

  const handleUploadFile = (files: FileList | File[]) => {
    setFileError(null)
    if (isValidFileArrayOrFileList(files)) {
      const payload = {
        file: files[0],
        type: documentType,
        documentTypeInput: label
      }
      uploadFile(payload, handleFileUploaded)
    }
  }

  const handleError = (error: UploadFileError) => setFileError(checkFileUpload(error))

  const handleFileRemove = () => {
    setFileError(null)
    if (file?.id) {
      removeFile(file.id)
      setFile(null)
      onChange(name, null)
    }
  }

  return (
    <div className={cn(styles['file-input-wrapper'], 'file-input-wrapper', className)} data-test={dataTest}>
      <div className={styles['file-input-label']}>
        {label}
        <span>*</span>
      </div>

      <DragAndDrop
        onDrop={handleUploadFile}
        onError={handleError}
        acceptType={settings.questionnaireFileTypes}
        maxFileSize={settings.questionnaireMaxFileSize}
      >
        <FileInput
          maxNumber={1}
          acceptType={settings.questionnaireFileTypes}
          maxFileSize={settings.questionnaireMaxFileSize}
          onChange={handleUploadFile}
          onError={handleError}
        >
          {({ onFileUpload, onRemoveAll }) =>
            file ? (
              <div className={cn(styles['file-input-container'], styles['file-input-selected'])}>
                <div className={styles['uploaded-file']}>
                  <UploadedIcon />
                  <div className='uploaded-file-name'>{file.fileName}</div>
                </div>
                <span onClick={handleFileRemove}>
                  <DeleteIcon />
                </span>
              </div>
            ) : (
              <div
                className={styles['file-input-container']}
                onClick={() => {
                  onRemoveAll()
                  onFileUpload()
                }}
                data-test={`${dataTest}-upload-file`}
              >
                <UploadIcon />
                <div>{tr('FRONTEND.FILE_UPLOAD.UPLOAD_BUTTON', 'Upload file')}</div>
              </div>
            )
          }
        </FileInput>
      </DragAndDrop>

      <ErrorDisplay error={[error, fileError, uploadFileError]} hideIcon />
    </div>
  )
}

export default FileUploadInput
